"use client";
import { animated, useSpring } from "@react-spring/web";
import { useEffect, useRef } from "react";
import { useAppContext } from "../../provider/AppProvider";

const size = 24;

const Cursor = ({ isVisible }: { isVisible: true }) => {
    const { cursorState, cursorCoordinates, cursorText } = useAppContext();

    const positionRef = useRef({ x: 0, y: 0, scale: 1 });

    const [{ x, y }, api] = useSpring(() => {
        return {
            x: positionRef.current.x,
            y: positionRef.current.y,
            config: { tension: 200, friction: 40, mass: 1 },
        };
    }, [positionRef, cursorState, cursorText]);

    const [{ scale }] = useSpring(() => {
        let scale;
        switch (cursorState) {
            case "default":
                scale = 1;
                break;
            case "hover":
                scale = 2;
                break;
            case "click":
                scale = 0;
                break;
            case "hidden":
                scale = 0;
                break;
        }
        return {
            scale,
        };
    }, [positionRef, cursorState]);

    const [{ scale: hoverScale }] = useSpring(
        () => ({
            scale: cursorState === "click" ? 1 : 0,
        }),
        [positionRef, cursorState],
    );

    useEffect(() => {
        function onMouseMove(e: MouseEvent) {
            api.start({ x: e.clientX, y: e.clientY });
            positionRef.current = { x: e.clientX, y: e.clientY, scale: 1 };
        }

        window.addEventListener("mousemove", onMouseMove, { passive: true });

        return () => {
            window.removeEventListener("mousemove", onMouseMove);
        };
    }, [api, cursorState]);

    return (
        <>
            <animated.div
                className="hidden md:block cursor"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: size,
                    height: size,
                    transformOrigin: "center center",
                    pointerEvents: "none",
                    userSelect: "none",
                    scale: scale,
                    x: x.to((xVal) => xVal - size / 2),
                    y: y.to((yVal) => yVal - size / 2),
                }}
            />

            <animated.div
                className="hidden md:block cursor-hover"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    transformOrigin: "top left",
                    pointerEvents: "none",
                    userSelect: "none",
                    scale: hoverScale,
                    x: x.to((xVal) => xVal - size / 2),
                    y: y.to((yVal) => yVal - size / 2),
                }}
            >
                <span className="block">{cursorText}</span>
            </animated.div>
        </>
    );
};

export default Cursor;
